import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  FunctionComponent,
} from "react";

import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Device, Call } from "@twilio/voice-sdk";
import { useNavigate, useMatch } from "react-router-dom";

import Logger from "../../lib/logger";
import { getApiUrl } from "../../lib/common/getApiUrl";
import {
  useCallSettings,
  useCheckFlowExtraButtons,
  useLayout,
  useReceptionistPhone,
} from "../../lib/hooks/useQueryBoard";

import { client } from "../../bit/board/hooks/useGraphQLClient";

import CallingModal from "./CallingModal";

import { useOfflineContext } from "../../context/Offline";
import { useCallActive } from "../../context/CallActive";
import { QUERY_ACCESS_EVENT } from "../../config/graphql/query";
import { useCheckinAccessNavigation } from "../../lib/hooks/useCheckinAccessNavigation";
import { useCallSounds } from "../../lib/hooks/useCallSounds";

const isDev = process.env.NODE_ENV === "development";

const CLIENT_TOKEN = isDev
  ? process.env.API_CLIENT_TOKEN
  : window.location.pathname.split("/")[2];

const apiUrl = getApiUrl();

const api = axios.create({
  baseURL: `${apiUrl}/api/v2`,
  headers: {
    "client-token": CLIENT_TOKEN,
  },
});

const Calling: FunctionComponent = React.memo(() => {
  const navigate = useNavigate();

  const {
    location: { pathname },
  } = window;

  const { online } = useOfflineContext();

  const { setCallActive } = useCallActive();

  const { ringDuration, maxDuration } = useCallSettings();

  const {
    startSound,
    ringingSound,
    connectedSound,
    endSound,
    playSound,
    stopAllSounds,
  } = useCallSounds();

  const { callReception } = useLayout();

  const callingOptions = useMemo(
    () => callReception?.callMenu?.options || [],
    [callReception],
  );

  const path = useMemo(() => {
    if (pathname.includes("access/option")) {
      return `access/option/:action/contact/:id/:callType`;
    }

    if (pathname.includes("delivery")) {
      return `${
        pathname.includes("custom") ? "custom-" : ""
      }delivery/:action/contact/:id/:callType`;
    }

    if (pathname.includes("access")) {
      return `access/checkin/${
        pathname.includes("manual") ? "manual/" : ""
      }contact/:id/:callType`;
    }

    if (pathname.includes("calling-menu")) {
      return `calling-menu/contact/:id/:callType`;
    }

    return "contact/:id/:callType";
  }, []);

  const match = useMatch({
    path,
  });

  const id = match?.params?.id;

  const callType = match?.params?.callType;

  const receptionistPhone = useReceptionistPhone();

  const extraButtons = useCheckFlowExtraButtons();

  const isCallingExtraButton = !!id?.startsWith("extra-");

  // a direct number is dialed, there is no access event behind it
  const isCallingReception =
    !!id?.startsWith("reception") || isCallingExtraButton;

  const { data: accessEventData } = useQuery(
    [`access/event/${id}`],
    () =>
      client.request<{ accessEvent: IAccessEvent }>(QUERY_ACCESS_EVENT, {
        id,
      }),
    {
      enabled: !!id && !isCallingReception,
      retry: false,
      staleTime: 1 * (60 * 1000),
      onError: () => {
        navigate("/", { replace: true });
      },
    },
  );

  const receiverPhone =
    accessEventData?.accessEvent?.receiver?.phone ||
    accessEventData?.accessEvent?.customReceiver?.phone;

  const accessEventType = accessEventData?.accessEvent?.type;

  const {
    handleCheckinAccessNavigation,
    willNavigateToCheckinAccessControlPage,
    isDoorOpening,
    accessSwitchModal,
  } = useCheckinAccessNavigation({
    type: accessEventType,
    id,
  });

  const phoneNumber = useMemo(() => {
    if (id === "reception") {
      return receptionistPhone;
    }

    if (isCallingExtraButton) {
      return extraButtons.find(
        (button) => button.id === id?.replace("extra-", ""),
      )?.phone;
    }

    if (!!id && /^reception-\d+$/.test(id)) {
      const index = parseInt(id.split("-")[1], 10);

      return callingOptions[index].phone;
    }

    return receiverPhone;
  }, [
    receptionistPhone,
    receiverPhone,
    id,
    callingOptions,
    isCallingExtraButton,
    extraButtons,
  ]);

  const timerRef = useRef<NodeJS.Timeout>();

  const call = useRef<Call>();
  const device = useRef<Device>();

  const [callingStatus, setCallingStatus] = useState<CallStatus>();

  const { data } = useQuery(
    ["token"],
    () =>
      api
        .get(`/twilio/token`)
        .then(({ data }) => data)
        .catch((error) => {
          Logger.error(`[device] - disconnect`, error);
        }),
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      staleTime: 0,
    },
  );

  const onEndCall = useCallback(() => {
    device.current?.disconnectAll();
    device.current?.destroy();
  }, []);

  const onClose = useCallback(() => {
    setCallingStatus(undefined);

    handleCheckinAccessNavigation();
  }, []);

  useEffect(() => {
    let ringingTimeout: NodeJS.Timeout;

    const init = async () => {
      if (!phoneNumber) {
        handleCheckinAccessNavigation();
        return;
      }

      if (!(data && data.token) || !online) {
        return;
      }

      device.current = new Device(data.token, {
        ...(data.edge ? { edge: data.edge } : {}),
        disableAudioContextSounds: true,
        sounds: {
          incoming: new URL("../../assets/sounds/silence.mp3", import.meta.url)
            .href,
          outgoing: new URL("../../assets/sounds/silence.mp3", import.meta.url)
            .href,
          disconnect: new URL(
            "../../assets/sounds/silence.mp3",
            import.meta.url,
          ).href,
        },
      });

      Logger.debug(`[device] Device connected`);
      Logger.debug(`[call] Establishing a call`);

      setCallActive(true);

      setCallingStatus("connecting");

      ringingTimeout = setTimeout(() => {
        setCallingStatus((prev) => {
          if (prev === "connecting") {
            Logger.debug(
              "[call] Fallback: forcing ringing state after timeout",
            );
            playSound(ringingSound);
            return "ringing";
          }
          return prev;
        });
      }, 2500);

      playSound(startSound);

      let params: Record<string, string> = {
        To: phoneNumber,
      };

      if (callType === "ping") {
        params = {
          ...params,
          Timeout: "10",
          Ping: "1",
        };
      } else {
        params = {
          ...params,
          Timeout: String(ringDuration),
          ...(maxDuration ? { TimeLimit: String(maxDuration) } : {}),
        };
      }

      call.current = await device.current.connect({
        params,
      });

      call.current.on("ringing", (call) => {
        if (call) {
          playSound(ringingSound);
          setCallingStatus("ringing");
        }
      });

      call.current.on("accept", () => {
        /**
         * If someone answers the "ping" call by chance,
         * end the call automatically
         */
        if (callType === "ping") {
          playSound(endSound);
          onEndCall();

          return;
        }
        playSound(connectedSound);
        setCallingStatus("open");
      });

      call.current.on("cancel", () => {
        Logger.debug(`[call] The call has been canceled.`);

        setCallActive(false);

        playSound(endSound);

        setCallingStatus("close");
      });

      call.current.on("disconnect", (call) => {
        Logger.debug(`[call] The call has been disconnected.`, call);

        setCallActive(false);

        playSound(endSound);

        setCallingStatus("close");
      });

      call.current.on("reject", () => {
        Logger.debug(`[call] The call was rejected.`);

        setCallActive(false);

        playSound(endSound);

        setCallingStatus("close");
      });

      call.current.on("error", (error) => {
        Logger.error(`[call] An error has occurred: `, error);

        // trackEvent({ type: "calling", value: "error" });
        setCallActive(false);
        playSound(endSound);
        setCallingStatus("error");
      });

      call.current.on("reconnected", () => {
        Logger.debug(`[call] The call has regained connectivity.`);
      });

      call.current.on("reconnecting", (error) => {
        Logger.debug(`[call] Connectivity error: `, error);
      });

      device.current.on("error", (twilioError) => {
        Logger.error(`[device] An error has occurred: `, twilioError);

        // trackEvent({ type: "calling", value: "error" });
        setCallActive(false);
        playSound(endSound);
        setCallingStatus("error");
      });
    };

    init();

    return () => {
      setCallActive(false);

      stopAllSounds();
      if (!device.current) {
        return;
      }

      clearTimeout(ringingTimeout);

      device.current.disconnectAll();
      device.current.destroy();
      Logger.debug(`[device] Device disconnected`);
    };
  }, [data, phoneNumber]);

  useEffect(() => {
    if (callingStatus === "close" || callingStatus === "error") {
      timerRef.current = setTimeout(() => {
        handleCheckinAccessNavigation();
      }, 5000);
    }

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [callingStatus]);

  if (isDoorOpening) {
    return accessSwitchModal;
  }

  return (
    <CallingModal
      status={callingStatus}
      isCallingReception={isCallingReception}
      willNavigateToAccessControl={willNavigateToCheckinAccessControlPage}
      onEndCall={onEndCall}
      onClose={onClose}
    />
  );
});

export default Calling;
